import { useMemo, useState, type FormEvent } from 'react';
import {
  zonesApi,
  zonePairsApi,
  type Zone,
  type ZonePair,
} from '@/api/client';
import { useAsync } from '@/hooks';
import { Layout } from '@/components/Layout';
import { DataTable } from '@/components/DataTable';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { ZonePolygonMap } from '@/components/ZonePolygonMap';
import { adminNavItems } from '@/pages/navItems';
import { useConfirm, useToast } from '@/components/Feedback';
import { FlashMessage, errorText, useFlash } from '@/components/FlashMessage';

export function AdminZones() {
  const { data: zones, loading, error, reload } = useAsync(() => zonesApi.list(), []);
  const { data: pairs, reload: reloadPairs } = useAsync(() => zonePairsApi.list(), []);

  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ code: '', name: '', coverage: '' });
  const [saving, setSaving] = useState(false);
  const { flash, ok: showOk, error: showError, clear: clearFlash } = useFlash();

  const [drawZone, setDrawZone] = useState<Zone | null>(null);
  const [existingGeo, setExistingGeo] = useState<{
    type: 'MultiPolygon';
    coordinates: number[][][][];
  } | null>(null);
  const [draftGeo, setDraftGeo] = useState<{
    type: 'MultiPolygon';
    coordinates: number[][][][];
  } | null>(null);
  const [mapKey, setMapKey] = useState(0);

  const [pairForm, setPairForm] = useState({
    originZoneId: '',
    destinationZoneId: '',
    oneWayDistanceKm: '',
    typicalDriveTimeMinutes: '',
    dispatchLeadTimeHours: '3',
  });

  const [showImport, setShowImport] = useState(false);
  const [importGeo, setImportGeo] = useState<unknown>(null);
  const [importFileName, setImportFileName] = useState('');
  const [importPreview, setImportPreview] = useState<Awaited<
    ReturnType<typeof zonesApi.previewGeoJsonImport>
  > | null>(null);
  const [importing, setImporting] = useState(false);

  const zoneMap = useMemo(
    () => Object.fromEntries((zones ?? []).map((z) => [z.id, z])),
    [zones],
  );

  const confirm = useConfirm();
  const toast = useToast();

  const handleAdd = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    clearFlash();
    try {
      await zonesApi.create({
        code: form.code,
        name: form.name,
        coverageDescription: form.coverage || undefined,
      });
      setShowAdd(false);
      setForm({ code: '', name: '', coverage: '' });
      toast(`Zone ${form.code} created`);
      showOk(`Zone ${form.code} created — next, click “Draw” to outline the area it covers`);
      reload();
    } catch (err) {
      showError(errorText(err, 'Could not create the zone.'));
    } finally {
      setSaving(false);
    }
  };

  const seed = async () => {
    setSaving(true);
    clearFlash();
    try {
      await zonesApi.seed();
      showOk('Standard zones Z1–Z7 added — draw the area for each one');
      reload();
    } catch (err) {
      showError(errorText(err, 'Could not add the standard zones.'));
    } finally {
      setSaving(false);
    }
  };

  const onPickGeoJson = async (file: File | null) => {
    if (!file) return;
    setImportFileName(file.name);
    setImportPreview(null);
    clearFlash();
    try {
      const text = await file.text();
      const parsed = JSON.parse(text) as unknown;
      setImportGeo(parsed);
      setImporting(true);
      const preview = await zonesApi.previewGeoJsonImport(parsed);
      setImportPreview(preview);
      setShowImport(true);
    } catch (err) {
      setImportGeo(null);
      showError(errorText(err, 'Could not read that map file. It must be a GeoJSON (.geojson or .json) file.'));
    } finally {
      setImporting(false);
    }
  };

  const applyImport = async () => {
    if (!importGeo || !importPreview) return;
    setImporting(true);
    clearFlash();
    try {
      const matchedIndexes = importPreview.rows
        .filter((r) => r.status === 'matched')
        .map((r) => r.index);
      const res = await zonesApi.applyGeoJsonImport(importGeo, matchedIndexes);
      showOk(
        `Imported ${res.applied} zone area${res.applied === 1 ? '' : 's'} from ${importFileName || 'the file'}` +
          (res.skipped ? ` · ${res.skipped} skipped` : ''),
      );
      setShowImport(false);
      setImportGeo(null);
      setImportPreview(null);
      setImportFileName('');
      reload();
    } catch (err) {
      showError(errorText(err, 'Could not import the zone areas.'));
    } finally {
      setImporting(false);
    }
  };

  const openDraw = async (zone: Zone) => {
    setSaving(true);
    clearFlash();
    try {
      const res = await zonesApi.getPolygon(zone.id);
      setExistingGeo(res.geojson);
      setDraftGeo(res.geojson);
      setDrawZone(zone);
      setMapKey((k) => k + 1);
    } catch (err) {
      showError(errorText(err, 'Could not load the zone area.'));
    } finally {
      setSaving(false);
    }
  };

  const savePolygon = async () => {
    if (!drawZone) return;
    if (!draftGeo) {
      const confirmed = await confirm({
        title: `Remove the area for ${drawZone.code}?`,
        message: `${drawZone.name} will have no map area, so addresses there won't be matched to this zone and bookings may be priced as outside your service zones until you draw it again.`,
        confirmLabel: 'Remove area',
        danger: true,
      });
      if (!confirmed) return;
    }
    setSaving(true);
    clearFlash();
    try {
      if (!draftGeo) {
        await zonesApi.clearPolygon(drawZone.id);
        showOk(`Removed the area for ${drawZone.code}`);
      } else {
        await zonesApi.savePolygon(drawZone.id, draftGeo);
        showOk(`Saved the area for ${drawZone.code}`);
      }
      setDrawZone(null);
      reload();
    } catch (err) {
      showError(errorText(err, 'Could not save the zone area.'));
    } finally {
      setSaving(false);
    }
  };

  const savePair = async (e: FormEvent) => {
    e.preventDefault();
    if (pairForm.originZoneId === pairForm.destinationZoneId) {
      showError('Origin and destination must be different zones.');
      return;
    }
    setSaving(true);
    clearFlash();
    try {
      await zonePairsApi.upsert({
        originZoneId: pairForm.originZoneId,
        destinationZoneId: pairForm.destinationZoneId,
        oneWayDistanceKm: pairForm.oneWayDistanceKm || undefined,
        typicalDriveTimeMinutes: pairForm.typicalDriveTimeMinutes
          ? Number(pairForm.typicalDriveTimeMinutes)
          : undefined,
        dispatchLeadTimeHours: pairForm.dispatchLeadTimeHours || undefined,
      });
      showOk('Zone pair saved');
      reloadPairs();
    } catch (err) {
      showError(errorText(err, 'Could not save the zone pair.'));
    } finally {
      setSaving(false);
    }
  };

  const drawnCount = (zones ?? []).filter((z) => z.hasPolygon).length;

  return (
    <Layout navItems={adminNavItems}>
      <div className="page">
        <div className="page-header row-between">
          <div>
            <p className="kicker">Geography</p>
            <h1>Service zones</h1>
            <p className="text-muted">
              Draw LGU polygons (not radius). Addresses resolve into these zones before rates or pay
              can work. {drawnCount}/{(zones ?? []).length || 0} drawn.
            </p>
          </div>
          <div className="row">
            <Button variant="secondary" loading={saving} onClick={seed}>Seed Z1–Z7</Button>
            <label className="btn-file">
              <input
                type="file"
                accept=".geojson,.json,application/geo+json,application/json"
                hidden
                onChange={(e) => {
                  void onPickGeoJson(e.target.files?.[0] ?? null);
                  e.target.value = '';
                }}
              />
              <Button
                type="button"
                variant="secondary"
                loading={importing}
                onClick={(e) => {
                  const input = (e.currentTarget.parentElement as HTMLLabelElement).querySelector('input');
                  input?.click();
                }}
              >
                Import GeoJSON
              </Button>
            </label>
            <Button onClick={() => setShowAdd(true)}>Add zone</Button>
          </div>
        </div>

        <FlashMessage flash={flash} />

        <section className="panel">
          <DataTable
            filterSort={false}
            columns={[
              { key: 'code', header: 'Code' },
              { key: 'name', header: 'Name' },
              {
                key: 'coverage',
                header: 'LGUs / coverage',
                render: (r: Zone) => r.coverageDescription ?? r.coverage ?? '—',
              },
              {
                key: 'polygon',
                header: 'Polygon',
                render: (r: Zone) =>
                  r.hasPolygon ? (
                    <span className="text-ok">Drawn</span>
                  ) : (
                    <span className="text-warning">Missing</span>
                  ),
              },
              {
                key: 'actions',
                header: '',
                render: (r: Zone) => (
                  <Button size="sm" variant="secondary" onClick={() => openDraw(r)}>
                    {r.hasPolygon ? 'Edit map' : 'Draw map'}
                  </Button>
                ),
              },
            ]}
            data={zones ?? []}
            keyField="id"
            loading={loading}
            loadError={error}
            dense
            emptyMessage="No zones. Seed Z1–Z7 or add a zone, then draw each LGU polygon."
          />
        </section>

        <section className="panel">
          <div className="row-between" style={{ marginBottom: '1rem' }}>
            <div>
              <h2>Zone pairs</h2>
              <p className="text-muted">
                Typical distance, drive time and how much notice dispatch needs between two zones.
                Used for pricing and scheduling.
              </p>
            </div>
          </div>

          <form className="pair-form" onSubmit={savePair}>
            <Select
              label="Origin"
              value={pairForm.originZoneId}
              onChange={(e) => setPairForm({ ...pairForm, originZoneId: e.target.value })}
              required
            >
              <option value="">Select…</option>
              {(zones ?? []).map((z) => (
                <option key={z.id} value={z.id}>{z.code} · {z.name}</option>
              ))}
            </Select>
            <Select
              label="Destination"
              value={pairForm.destinationZoneId}
              onChange={(e) => setPairForm({ ...pairForm, destinationZoneId: e.target.value })}
              required
            >
              <option value="">Select…</option>
              {(zones ?? []).filter((z) => z.id !== pairForm.originZoneId).map((z) => (
                <option key={z.id} value={z.id}>{z.code} · {z.name}</option>
              ))}
            </Select>
            <Input
              label="One-way km"
              type="number"
              step="0.1"
              min="0"
              value={pairForm.oneWayDistanceKm}
              onChange={(e) => setPairForm({ ...pairForm, oneWayDistanceKm: e.target.value })}
            />
            <Input
              label="Drive time (min)"
              type="number"
              min="0"
              value={pairForm.typicalDriveTimeMinutes}
              onChange={(e) => setPairForm({ ...pairForm, typicalDriveTimeMinutes: e.target.value })}
            />
            <Input
              label="Dispatch lead (hrs)"
              type="number"
              step="0.5"
              min="0"
              value={pairForm.dispatchLeadTimeHours}
              onChange={(e) => setPairForm({ ...pairForm, dispatchLeadTimeHours: e.target.value })}
            />
            <Button type="submit" loading={saving}>Save pair</Button>
          </form>

          <DataTable
            filterSort={false}
            data={pairs ?? []}
            keyField="id"
            dense
            emptyMessage="No zone pairs yet. Enter lane distances after polygons exist."
            columns={[
              {
                key: 'origin',
                header: 'Origin',
                render: (r: ZonePair) =>
                  zoneMap[r.originZoneId]
                    ? `${zoneMap[r.originZoneId].code}`
                    : '—',
              },
              {
                key: 'dest',
                header: 'Destination',
                render: (r: ZonePair) =>
                  zoneMap[r.destinationZoneId]
                    ? `${zoneMap[r.destinationZoneId].code}`
                    : '—',
              },
              {
                key: 'km',
                header: 'km',
                render: (r) => r.oneWayDistanceKm ?? '—',
              },
              {
                key: 'mins',
                header: 'Drive min',
                render: (r) => r.typicalDriveTimeMinutes ?? '—',
              },
              {
                key: 'lead',
                header: 'Lead hrs',
                render: (r) => r.dispatchLeadTimeHours ?? '—',
              },
            ]}
          />
        </section>

        <Modal open={showAdd} onClose={() => setShowAdd(false)} title="Add zone">
          <form onSubmit={handleAdd}>
            <Input
              label="Code"
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value })}
              required
              placeholder="Z8"
            />
            <Input
              label="Name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
            <Input
              label="LGU coverage description"
              value={form.coverage}
              onChange={(e) => setForm({ ...form, coverage: e.target.value })}
              placeholder="Cities and municipalities in this zone"
            />
            {showAdd && flash?.kind === 'error' && <FlashMessage flash={flash} />}
            <Button type="submit" loading={saving}>Create zone</Button>
          </form>
        </Modal>

        <Modal
          open={showImport}
          onClose={() => {
            setShowImport(false);
            setImportPreview(null);
            setImportGeo(null);
          }}
          title={importFileName ? `Import GeoJSON · ${importFileName}` : 'Import GeoJSON'}
          size="lg"
        >
          {importPreview && (
            <>
              <p className="text-muted">
                {importPreview.matched} matched · {importPreview.unmatched} unmatched ·{' '}
                {importPreview.invalid} invalid of {importPreview.featureCount} feature(s). Features
                match zones by <code>properties.code</code> (Z1–Z7) or <code>properties.name</code>.
                Historical trips are not re-rated.
              </p>
              <DataTable
                filterSort={false}
                dense
                keyField="index"
                data={importPreview.rows}
                emptyMessage="No features in file."
                columns={[
                  { key: 'index', header: '#', render: (r) => r.index + 1 },
                  { key: 'featureLabel', header: 'Feature' },
                  {
                    key: 'zone',
                    header: 'Matched zone',
                    render: (r) =>
                      r.matchedZoneCode
                        ? `${r.matchedZoneCode} · ${r.matchedZoneName ?? ''}`
                        : '—',
                  },
                  { key: 'geometryType', header: 'Geometry', render: (r) => r.geometryType ?? '—' },
                  {
                    key: 'status',
                    header: 'Status',
                    render: (r) => (
                      <span
                        className={
                          r.status === 'matched'
                            ? 'text-ok'
                            : r.status === 'unmatched'
                              ? 'text-warning'
                              : 'text-urgent'
                        }
                      >
                        {r.status}
                        {r.error ? ` — ${r.error}` : ''}
                      </span>
                    ),
                  },
                ]}
              />
              <div className="row" style={{ justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setShowImport(false);
                    setImportPreview(null);
                    setImportGeo(null);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  loading={importing}
                  disabled={!importPreview.matched}
                  onClick={applyImport}
                >
                  Apply {importPreview.matched} polygon{importPreview.matched === 1 ? '' : 's'}
                </Button>
              </div>
            </>
          )}
        </Modal>

        <Modal
          open={!!drawZone}
          onClose={() => setDrawZone(null)}
          title={drawZone ? `Draw polygon · ${drawZone.code} ${drawZone.name}` : 'Draw polygon'}
          size="lg"
        >
          {drawZone && (
            <>
              <ZonePolygonMap
                key={`${drawZone.id}-${mapKey}`}
                initialGeoJson={existingGeo}
                onChange={setDraftGeo}
              />
              {flash?.kind === 'error' && <FlashMessage flash={flash} />}
              <div className="row" style={{ justifyContent: 'flex-end', gap: '0.5rem' }}>
                <Button type="button" variant="ghost" onClick={() => setDrawZone(null)}>Cancel</Button>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setDraftGeo(null);
                    setExistingGeo(null);
                    setMapKey((k) => k + 1);
                  }}
                >
                  Clear shape
                </Button>
                <Button loading={saving} onClick={savePolygon}>
                  {draftGeo ? 'Save area' : 'Save with no area'}
                </Button>
              </div>
            </>
          )}
        </Modal>

        <style>{`
          .panel {
            background: var(--color-surface);
            border: 1px solid var(--color-border);
            border-radius: var(--radius-md);
            padding: var(--space-lg);
            margin-bottom: var(--space-lg);
          }
          .panel h2 { margin-bottom: var(--space-xs); }
          .pair-form {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: var(--space-md);
            align-items: end;
            margin-bottom: var(--space-lg);
          }
          @media (max-width: 900px) {
            .pair-form { grid-template-columns: 1fr; }
          }
        `}</style>
      </div>
    </Layout>
  );
}
